<template>
  <div class="otp-input-container" @paste="handlePaste">
    <div v-for="(_, index) in numInputs" :key="index" class="otp-input-wrapper">
      <input
        ref="otpInputs"
        :value="digits[index]"
        @input="(e) => handleInput(e, index)"
        @keydown="(e) => handleKeydown(e, index)"
        maxlength="1"
        type="tel"
        inputmode="numeric"
        autocomplete="off"
      />
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  ref,
  onMounted,
  nextTick,
  getCurrentInstance,
  toRefs,
  watch,
} from "vue";

export default defineComponent({
  name: "OtpInput",
  props: {
    /** Number of digits (input boxes) to render */
    numInputs: {
      type: Number,
      default: 6,
    },
    /** Auto-focus the first box on mount */
    shouldAutoFocus: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["on-complete", "on-change"],
  setup(props, { emit }) {
    const { numInputs } = toRefs(props);
    const digits = ref(Array(numInputs.value).fill(""));
    const { proxy } = getCurrentInstance();

    const focusInput = (index) => {
      const inputsArray = proxy.$refs.otpInputs;
      if (inputsArray && inputsArray[index]) {
        inputsArray[index].focus();
      }
    };

    const handleInput = (e, index) => {
      const raw = e.target.value;
      // Keep only the last character
      let char = raw.slice(-1);
      // If not a digit, clear it
      if (!/^[0-9]$/.test(char)) {
        char = "";
      }
      digits.value[index] = char;
      e.target.value = char;
      emit("on-change", digits.value.join(""));
      if (char && index < numInputs.value - 1) {
        focusInput(index + 1);
      }
      if (digits.value.every((d) => d !== "")) {
        emit("on-complete", digits.value.join(""));
      }
    };

    const handleKeydown = (e, index) => {
      if (e.key === "Backspace") {
        if (digits.value[index] === "") {
          if (index > 0) {
            focusInput(index - 1);
          }
        } else {
          // Clear the model
          digits.value[index] = "";
          //TODO: CONFIRM THIS
          // Clear the visible character in the <input> DOM node
          e.target.value = "";
          emit("on-change", digits.value.join(""));
        }
      }
    };

    const handlePaste = (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData("text").trim();
      const chars = pastedData
        .split("")
        .filter((ch) => /^[0-9]$/.test(ch))
        .slice(0, numInputs.value);

      chars.forEach((ch, idx) => {
        digits.value[idx] = ch;
        const inputsArray = proxy.$refs.otpInputs;
        if (inputsArray && inputsArray[idx]) {
          inputsArray[idx].value = ch;
        }
      });

      emit("on-change", digits.value.join(""));

      nextTick(() => {
        const nextIdx =
          chars.length >= numInputs.value ? numInputs.value - 1 : chars.length;
        focusInput(nextIdx);
        if (chars.length >= numInputs.value) {
          emit("on-complete", digits.value.join(""));
        }
      });
    };

    onMounted(() => {
      if (props.shouldAutoFocus) {
        focusInput(0);
      }
    });

    watch(numInputs, (newCount) => {
      digits.value = Array(newCount).fill("");
    });

    return {
      digits,
      handleInput,
      handleKeydown,
      handlePaste,
    };
  },
});
</script>

<style scoped>
.otp-input-container {
  display: flex;
  width: 100%;
  gap: 0.5rem;
}

.otp-input-wrapper {
  flex: 1;
}

.otp-input-wrapper input {
  width: 100%;
  /* Maintain a square aspect ratio */
  aspect-ratio: 1 / 1;
  text-align: center;
  font-size: 2rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  outline: none;
}

.otp-input-wrapper input:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.2);
}
</style>
